import { useEffect, useState } from "react";
import { AlertTriangle, Check, ChevronRight, Star } from "lucide-react";
import api from "../lib/api";
import { SecondaryAction } from "./kit/SecondaryAction";
import { RatingAxisFields } from "./FacilityRatings";
import { useGroundFacilities } from "./FacilityPicker";
import {
  draftFromOwnRating,
  facilityRatingPath,
  ratingBodyFromDraft,
  ratingDraftComplete,
} from "../lib/facilityRatings";
import {
  RATE_ROW_TITLE,
  RATE_ROW_SUB,
  RATE_OPEN_TITLE,
  RATE_HIDE,
  RATE_OPTIONAL_LINE,
  RATE_SAVE,
  RATE_SAVING,
  RATE_CLEAR,
  RATE_SAVED,
  RATE_CHANGE,
  RATE_FAILED,
  rateNoun,
} from "../lib/logSavedStep";

/**
 * Issue #1029 (#842 part B) — step 3's optional rating of the field or net
 * picked on step 2, with its OWN save. `SavedRating.dc.html`
 * (design-round-6b, D204.1) is the drawing.
 *
 * WHY ITS OWN SAVE. The match is already saved when this paints, so the
 * played-here gate (`facilityRatings.ts`) is met, and a failed rating can
 * never be mistaken for a failed match. The old dialog rated after the
 * match POST inside one submit (`PerformanceTracking.jsx`); that path is
 * untouched.
 *
 * REUSE. The chips are the grounds map's own `RatingAxisFields`, the body is
 * `ratingBodyFromDraft`, the URL is `facilityRatingPath` — so Turn & grip
 * shows and travels on turf only, with no per-surface rule copied here.
 *
 * Renders nothing unless the match names a facility. A failure stays on
 * the page (D57), glyph and rule in the alert token and text in ink (D55);
 * a success collapses to one line in place (D109) — no toast either way.
 * The save is the navy outline, never gold (R-003).
 */
const rowBox = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
  minHeight: 52, padding: "10px 14px",
  background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)",
};
const linkButton = {
  background: "none", border: 0, padding: 0, cursor: "pointer",
  fontSize: 13, fontWeight: 600, color: "var(--nw-navy)",
};

/** The refused-save line: D57 (on the page, no toast), D55 (the alert token
 * on the glyph and the rule only, the words in ink). */
export function RatingFailure() {
  return (
    <div
      role="alert"
      data-testid="m-facility-rating-error"
      style={{
        display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, lineHeight: 1.5,
        color: "var(--nw-ink)", padding: "10px 12px",
        background: "var(--nw-line-faint)", borderLeft: "3px solid var(--nw-alert)", borderRadius: 10,
      }}
    >
      <AlertTriangle size={16} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
      <span>{RATE_FAILED}</span>
    </div>
  );
}

export default function LogMatchSavedRating({ match, facilityKind }) {
  const groundId = match?.ground_id;
  const facilityId = match?.facility_id;
  const name = match?.facility_name || "";
  const surface = match?.facility_surface;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => draftFromOwnRating(null));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // What the server holds, for Hide to restore (own rating on mount, or the last save).
  const [savedDraft, setSavedDraft] = useState(() => draftFromOwnRating(null));
  const [failed, setFailed] = useState(false);

  // The match carries the facility's name and surface but not its kind; the
  // venue's own surface list does. Unreadable or not yet loaded reads as the
  // neutral "surface", never a guess.
  const facilities = useGroundFacilities(facilityId && !facilityKind ? groundId : null);
  const noun = rateNoun(facilityKind || facilities.find((f) => f.facility_id === facilityId)?.kind);

  // "Change it" on a fresh mount: read the caller's own rating once and
  // pre-fill the draft with it — but never over scores already chosen.
  useEffect(() => {
    if (!groundId || !facilityId) return undefined;
    let cancelled = false;
    api
      .get(`/grounds/${groundId}/ratings`)
      .then((r) => {
        const own = (r.data?.facilities || []).find((f) => f.facility_id === facilityId)?.my_rating;
        if (cancelled || !own) return;
        setSavedDraft(draftFromOwnRating(own));
        setDraft((d) => (Object.values(d).every((v) => v == null) ? draftFromOwnRating(own) : d));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [groundId, facilityId]);

  if (!groundId || !facilityId) return null;

  const complete = ratingDraftComplete(draft);
  const anyChosen = Object.values(draft).some((v) => v != null);

  const save = async () => {
    setSaving(true);
    setFailed(false);
    const sent = draft;
    try {
      await api.put(facilityRatingPath(groundId, facilityId), ratingBodyFromDraft(sent, surface));
      setSavedDraft(sent);
      // The scores the server now holds, so "Change it" reopens on them even
      // if the draft was reset while the request was in flight.
      setDraft(sent);
      setSaved(true);
      setOpen(false);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  // Hide discards the unsaved draft (back to the saved scores, or empty) and
  // the failure with it: reopening shows what the server holds, no error.
  const hide = () => {
    setOpen(false);
    setFailed(false);
    setDraft(savedDraft);
  };

  const star = <Star size={18} style={{ color: "var(--nw-navy)", flexShrink: 0 }} aria-hidden="true" />;

  if (saved && !open) {
    return (
      <div data-testid="m-facility-rating-block" style={rowBox}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <Check size={18} strokeWidth={2.5} style={{ color: "var(--nw-ok)", flexShrink: 0 }} aria-hidden="true" />
          <span data-testid="m-facility-rating-saved" style={{ fontSize: 14, lineHeight: 1.45, color: "var(--nw-ink)" }}>
            {RATE_SAVED(name)}
          </span>
        </div>
        <button
          type="button"
          data-testid="m-facility-rating-open"
          onClick={() => setOpen(true)}
          style={{ ...linkButton, whiteSpace: "nowrap" }}
        >
          {RATE_CHANGE}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div data-testid="m-facility-rating-block">
        <button
          type="button"
          data-testid="m-facility-rating-open"
          onClick={() => setOpen(true)}
          style={{ ...rowBox, width: "100%", textAlign: "left", cursor: "pointer" }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {star}
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{RATE_ROW_TITLE(noun)}</span>
              <span style={{ fontSize: 12, lineHeight: 1.45, color: "var(--nw-ink-muted)" }}>{RATE_ROW_SUB(name)}</span>
            </span>
          </span>
          <ChevronRight size={20} style={{ color: "var(--nw-ink-faint)", flexShrink: 0 }} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="m-facility-rating-block"
      style={{
        display: "flex", flexDirection: "column", gap: 12, padding: 14,
        background: "var(--nw-surface)", border: "1px solid var(--nw-navy)", borderRadius: "var(--nw-radius-card)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {star}
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
            {RATE_OPEN_TITLE(noun)}
          </div>
        </div>
        <button type="button" data-testid="m-facility-rating-collapse" disabled={saving} onClick={hide} style={{ ...linkButton, ...(saving ? { cursor: "not-allowed", color: "var(--nw-ink-faint)" } : null) }}>
          {RATE_HIDE}
        </button>
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>{RATE_OPTIONAL_LINE}</div>

      <RatingAxisFields surface={surface} draft={draft} setDraft={setDraft} saving={saving} testId="m-facility-rating-editor" tone="saved" />

      {failed && <RatingFailure />}

      <SecondaryAction
        variant="navy-outline"
        data-testid="m-facility-rating-save"
        disabled={!complete || saving}
        onClick={save}
      >
        {saving ? RATE_SAVING : RATE_SAVE}
      </SecondaryAction>
      {anyChosen && !saving && (
        <button
          type="button"
          data-testid="m-facility-rating-clear"
          disabled={saving}
          onClick={() => setDraft(draftFromOwnRating(null))}
          style={{ ...linkButton, alignSelf: "flex-start" }}
        >
          {RATE_CLEAR}
        </button>
      )}
    </div>
  );
}
